/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2023, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Injector,
  ViewChild,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogContentPlaceholderDirective } from './dialog-content-placeholder.directive';
import { DialogConfig, DialogRef } from '../types';

/**
 * Component for use when showing a standard aero dialog.
 */
@Component({
  selector: 'aero-dialog',
  templateUrl: './dialog.component.html',
  standalone: false,
})
export class DialogComponent implements AfterViewInit {

  componentRef: ComponentRef<any>;

  @ViewChild(DialogContentPlaceholderDirective) insertionPoint: DialogContentPlaceholderDirective;

  primaryDisabled: boolean;

  secondaryDisabled: boolean;

  config: DialogConfig;

  constructor(
    public dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig,
    private componentFactoryResolver: ComponentFactoryResolver,
    private cd: ChangeDetectorRef,
  ) {
    this.config = dialogConfig as DialogConfig;
  }

  ngAfterViewInit(): void {
    const aeroConfig = this.config as DialogConfig;

    this.primaryDisabled = aeroConfig.primaryDisabled;
    this.secondaryDisabled = aeroConfig.secondaryDisabled;

    if (aeroConfig.customContentComponent) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(aeroConfig.customContentComponent);

      const viewContainerRef = this.insertionPoint?.viewContainerRef;
      viewContainerRef?.clear();

      const parentInjector = viewContainerRef.injector;
      const that = this;

      const aeroDialogRef = <DialogRef>{
        setPrimaryDisabled(disabled: boolean) : void {
          that.primaryDisabled = disabled;
        },
        setSecondaryDisabled(disabled: boolean) : void {
          that.secondaryDisabled = disabled;
        },
        getDynamicDialogRef() : DynamicDialogRef {
          return that.dialogRef;
        },
        close(result? : any) : void {
          that.dialogRef.close(result);
        },
      };

      const injector = Injector.create({
        providers: [{ provide: 'DialogRef', useValue: aeroDialogRef }],
        parent: parentInjector,
      });

      this.componentRef = viewContainerRef?.createComponent(componentFactory, 0, injector);
    }
    this.cd.detectChanges();
  }

  /**
   * Called whenever input value changes.
   * @param $event input event
   */
  handleInputModelChanges($event) {
    this.config.dialogInput.inputText = $event.viewModel;
    this.config.dialogInput.onInputChange($event.viewModel);
    this.primaryDisabled = this.config.dialogInput.inputText === '';
  }
}
